import React, { useState } from 'react';
import { FileTextIcon, MailIcon, GavelIcon, LayoutIcon, EditIcon, EyeIcon, CopyIcon, TrashIcon, PlusIcon, FileIcon, ReceiptIcon, BellIcon, ImageIcon, TypeIcon, LanguagesIcon, BookIcon } from 'lucide-react';
export const DocumentTemplatesSettings = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  return <div className="space-y-8" data-id="element-929">
      {/* Bill & Receipt Templates */}
      <div className="border-b border-gray-200 pb-8" data-id="element-930">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-931">
          Bill & Receipt Templates
        </h3>
        <div className="space-y-6" data-id="element-932">
          {/* Bill Templates */}
          <div data-id="element-933">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-934">
              Bill Template Management
            </label>
            <div className="space-y-4" data-id="element-935">
              {[{
              name: 'Standard Property Tax Bill',
              description: 'Default template for property tax bills',
              sections: ['Header', 'Property Details', 'Tax Breakdown', 'Payment Instructions'],
              lastModified: '2024-02-15'
            }, {
              name: 'Utility Services Bill',
              description: 'Combined template for water and waste services',
              sections: ['Header', 'Usage Details', 'Charges Breakdown', 'Payment Methods'],
              lastModified: '2024-02-10'
            }].map(template => <div key={template.name} className="border border-gray-200 rounded-lg p-4" data-id="element-936">
                  <div className="flex items-center justify-between mb-3" data-id="element-937">
                    <div data-id="element-938">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-939">
                        {template.name}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-940">
                        {template.description}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-941">
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-942">
                        <EyeIcon className="h-4 w-4" data-id="element-943" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-944">
                        <EditIcon className="h-4 w-4" data-id="element-945" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-946">
                        <CopyIcon className="h-4 w-4" data-id="element-947" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-948">
                    <div className="flex flex-wrap gap-2" data-id="element-949">
                      {template.sections.map(section => <span key={section} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded" data-id="element-950">
                          {section}
                        </span>)}
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-951">
                      Last modified: {template.lastModified}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Receipt Templates */}
          <div data-id="element-952">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-953">
              Receipt Template Management
            </label>
            <div className="space-y-4" data-id="element-954">
              {[{
              name: 'Standard Payment Receipt',
              description: 'General purpose payment receipt template',
              elements: ['Transaction ID', 'Payment Details', 'Official Seal', 'QR Code'],
              format: 'A4'
            }, {
              name: 'Compact Receipt',
              description: 'Condensed format for point-of-service payments',
              elements: ['Basic Details', 'Amount', 'Reference Number'],
              format: '80mm'
            }].map(template => <div key={template.name} className="border border-gray-200 rounded-lg p-4" data-id="element-955">
                  <div className="flex items-center justify-between mb-3" data-id="element-956">
                    <div data-id="element-957">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-958">
                        {template.name}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-959">
                        {template.description}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500" data-id="element-960">
                      Format: {template.format}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-961">
                    {template.elements.map(element => <span key={element} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded" data-id="element-962">
                        {element}
                      </span>)}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Notification Templates */}
      <div className="border-b border-gray-200 pb-8" data-id="element-963">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-964">
          Notification Templates
        </h3>
        <div className="space-y-6" data-id="element-965">
          {/* Email Templates */}
          <div data-id="element-966">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-967">
              Email Notification Templates
            </label>
            <div className="space-y-4" data-id="element-968">
              {[{
              name: 'Payment Due Reminder',
              type: 'email',
              languages: ['English', 'Amharic', 'Oromo'],
              variables: ['[DUE_DATE]', '[AMOUNT]', '[PROPERTY_ID]']
            }, {
              name: 'Payment Confirmation',
              type: 'email',
              languages: ['English', 'Amharic', 'Oromo'],
              variables: ['[PAYMENT_ID]', '[AMOUNT]', '[DATE]']
            }].map(template => <div key={template.name} className="border border-gray-200 rounded-lg p-4" data-id="element-969">
                  <div className="flex items-center justify-between mb-3" data-id="element-970">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-971">
                      {template.name}
                    </h4>
                    <div className="flex items-center space-x-2" data-id="element-972">
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-973">
                        <EditIcon className="h-4 w-4" data-id="element-974" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-975">
                        <EyeIcon className="h-4 w-4" data-id="element-976" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-3" data-id="element-977">
                    <div className="flex flex-wrap gap-2" data-id="element-978">
                      {template.languages.map(lang => <span key={lang} className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded" data-id="element-979">
                          {lang}
                        </span>)}
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-980">
                      {template.variables.map(variable => <span key={variable} className="px-2 py-1 text-xs bg-purple-100 text-purple-600 rounded" data-id="element-981">
                          {variable}
                        </span>)}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* SMS Templates */}
          <div data-id="element-982">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-983">
              SMS Notification Templates
            </label>
            <div className="space-y-4" data-id="element-984">
              {[{
              name: 'Payment Reminder (SMS)',
              maxLength: 160,
              languages: ['English', 'Amharic'],
              variables: ['[AMT]', '[DATE]']
            }, {
              name: 'Payment Receipt (SMS)',
              maxLength: 160,
              languages: ['English', 'Amharic'],
              variables: ['[REF]', '[AMT]']
            }].map(template => <div key={template.name} className="border border-gray-200 rounded-lg p-4" data-id="element-985">
                  <div className="flex items-center justify-between mb-3" data-id="element-986">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-987">
                      {template.name}
                    </h4>
                    <span className="text-xs text-gray-500" data-id="element-988">
                      Max: {template.maxLength} chars
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-989">
                    <div className="flex flex-wrap gap-2" data-id="element-990">
                      {template.languages.map(lang => <span key={lang} className="px-2 py-1 text-xs bg-green-100 text-green-600 rounded" data-id="element-991">
                          {lang}
                        </span>)}
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-992">
                      {template.variables.map(variable => <span key={variable} className="px-2 py-1 text-xs bg-orange-100 text-orange-600 rounded" data-id="element-993">
                          {variable}
                        </span>)}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Legal & Agreement Templates */}
      <div className="border-b border-gray-200 pb-8" data-id="element-994">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-995">
          Legal & Agreement Templates
        </h3>
        <div className="space-y-6" data-id="element-996">
          {/* Legal Notices */}
          <div data-id="element-997">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-998">
              Legal Notice Templates
            </label>
            <div className="space-y-4" data-id="element-999">
              {[{
              name: 'Property Tax Default Notice',
              type: 'legal',
              sections: ['Notice Header', 'Default Details', 'Legal Consequences', 'Response Instructions'],
              requiresReview: true
            }, {
              name: 'Service Disconnection Notice',
              type: 'legal',
              sections: ['Notice Header', 'Service Details', 'Payment Required', 'Appeal Process'],
              requiresReview: true
            }].map(template => <div key={template.name} className="border border-gray-200 rounded-lg p-4" data-id="element-1000">
                  <div className="flex items-center justify-between mb-3" data-id="element-1001">
                    <div data-id="element-1002">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-1003">
                        {template.name}
                      </h4>
                      {template.requiresReview && <span className="text-xs text-red-600" data-id="element-1004">
                          Requires legal review
                        </span>}
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-1005">
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-1006">
                        <EditIcon className="h-4 w-4" data-id="element-1007" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-1008">
                        <EyeIcon className="h-4 w-4" data-id="element-1009" />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-1010">
                    {template.sections.map(section => <span key={section} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded" data-id="element-1011">
                        {section}
                      </span>)}
                  </div>
                </div>)}
            </div>
          </div>
          {/* Payment Agreements */}
          <div data-id="element-1012">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1013">
              Payment Agreement Templates
            </label>
            <div className="space-y-4" data-id="element-1014">
              {[{
              name: 'Standard Installment Agreement',
              type: 'agreement',
              sections: ['Terms', 'Payment Schedule', 'Default Conditions', 'Signatures'],
              variables: ['[TOTAL_AMOUNT]', '[INSTALLMENT_COUNT]', '[MONTHLY_AMOUNT]']
            }, {
              name: 'Hardship Payment Plan',
              type: 'agreement',
              sections: ['Qualification', 'Modified Terms', 'Review Period', 'Signatures'],
              variables: ['[ORIGINAL_AMOUNT]', '[ADJUSTED_AMOUNT]', '[TERM_LENGTH]']
            }].map(template => <div key={template.name} className="border border-gray-200 rounded-lg p-4" data-id="element-1015">
                  <div className="flex items-center justify-between mb-3" data-id="element-1016">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1017">
                      {template.name}
                    </h4>
                    <div className="flex items-center space-x-2" data-id="element-1018">
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-1019">
                        <EditIcon className="h-4 w-4" data-id="element-1020" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-1021">
                        <EyeIcon className="h-4 w-4" data-id="element-1022" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-3" data-id="element-1023">
                    <div className="flex flex-wrap gap-2" data-id="element-1024">
                      {template.sections.map(section => <span key={section} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded" data-id="element-1025">
                          {section}
                        </span>)}
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1026">
                      {template.variables.map(variable => <span key={variable} className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded" data-id="element-1027">
                          {variable}
                        </span>)}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      {/* Report Layout Templates */}
      <div className="pt-8" data-id="element-1028">
        <h3 className="text-base font-semibold text-gray-900 mb-6" data-id="element-1029">
          Report Layout Templates
        </h3>
        <div className="space-y-6" data-id="element-1030">
          {/* Header/Footer Configuration */}
          <div data-id="element-1031">
            <label className="block text-sm font-medium text-gray-700 mb-4" data-id="element-1032">
              Report Header & Footer Configuration
            </label>
            <div className="space-y-4" data-id="element-1033">
              {[{
              name: 'Standard Report Layout',
              elements: {
                header: ['Logo', 'Department Name', 'Report Title'],
                footer: ['Page Number', 'Generation Date', 'Confidentiality Notice']
              }
            }, {
              name: 'Financial Report Layout',
              elements: {
                header: ['Logo', 'Financial Year', 'Report Title', 'Department'],
                footer: ['Page Number', 'Approval Status', 'Confidentiality Level']
              }
            }].map(layout => <div key={layout.name} className="border border-gray-200 rounded-lg p-4" data-id="element-1034">
                  <div className="flex items-center justify-between mb-4" data-id="element-1035">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-1036">
                      {layout.name}
                    </h4>
                    <div className="flex items-center space-x-2" data-id="element-1037">
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-1038">
                        <EditIcon className="h-4 w-4" data-id="element-1039" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-500" data-id="element-1040">
                        <EyeIcon className="h-4 w-4" data-id="element-1041" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-3" data-id="element-1042">
                    <div data-id="element-1043">
                      <h5 className="text-xs font-medium text-gray-700 mb-2" data-id="element-1044">
                        Header Elements
                      </h5>
                      <div className="flex flex-wrap gap-2" data-id="element-1045">
                        {layout.elements.header.map(element => <span key={element} className="px-2 py-1 text-xs bg-green-100 text-green-600 rounded" data-id="element-1046">
                            {element}
                          </span>)}
                      </div>
                    </div>
                    <div data-id="element-1047">
                      <h5 className="text-xs font-medium text-gray-700 mb-2" data-id="element-1048">
                        Footer Elements
                      </h5>
                      <div className="flex flex-wrap gap-2" data-id="element-1049">
                        {layout.elements.footer.map(element => <span key={element} className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded" data-id="element-1050">
                            {element}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
};